const API_BASE_URL = '/api';



class OpenAIService {
  /**
   * Upload Excel file for analysis
   * POST /api/surveys/import-excel/
   */
  async uploadExcelFile(file, program, school) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('program', program);
    formData.append('school', school);

    try {
      const response = await fetch(`${API_BASE_URL}/surveys/import-excel`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || 'Upload failed');
      }

      const data = await response.json();
      return {
        analysisId: data.analysis_id,
        status: data.status,
        message: data.message
      };
    } catch (error) {
      console.error('Error uploading Excel file:', error);
      throw error;
    }
  }

  /**
   * Upload multiple Excel files for batch analysis
   * POST /api/surveys/batch-import/
   */
// <<<<<<< HEAD
  async batchImportExcel(files, analysisType, courseMapping, programName, school) {
    // Validate school parameter
    if (!school || school.trim() === '') {
      const proceed = await this.showSchoolMissingWarning();
      if (!proceed) {
        throw new Error('Upload cancelled: School information required');
      }
    }

    const formData = new FormData();
    
    // Add files
    files.forEach(file => {
      formData.append('files', file);
    });
    
    // Add required parameters
    formData.append('analysis_type', analysisType);
    formData.append('school', school);
    
    // Add program name for by_program analysis
    if (analysisType === 'by_program' && programName) {
      formData.append('program_name', programName);
    }
    
    // Add course mapping for by_course analysis
    if (analysisType === 'by_course' && courseMapping && Object.keys(courseMapping).length > 0) {
      formData.append('course_mapping', JSON.stringify(courseMapping));
    }

    try {
      const response = await fetch(`${API_BASE_URL}/surveys/batch-import`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Batch upload failed');
      }

      const data = await response.json();
      return {
        batchId: data.batch_id,
        totalFiles: data.total_files,
        status: data.status,
        message: data.message
      };
    } catch (error) {
      console.error('Error in batch upload:', error);
      throw error;
    }
  }

  /**
   * Get batch analysis status
   * POST /api/surveys/batch-status/
   */
  async getBatchAnalysisStatus(batchId, school) {
    try {
      const response = await fetch(`${API_BASE_URL}/surveys/batch-status`, {
// =======
//   async getAnalysisStatus(analysisId, school) {
//     try {
//       const response = await fetch(`${API_BASE_URL}/surveys/analysis/${analysisId}`, {
// >>>>>>> origin/main
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          batch_id: batchId 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get batch status');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting batch status:', error);
      throw error;
    }
  }

  /**
   * Poll for batch completion with individual file progress
   * Polls every 2-3 seconds until all files are completed or failed
   */
  async pollForBatchCompletion(batchId, school, onProgressUpdate = null) {
    const maxAttempts = 200; // ~10 minutes max (200 * 3 seconds)
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const batchStatus = await this.getBatchAnalysisStatus(batchId, school);
        
        // Call progress update callback if provided
        if (onProgressUpdate) {
          onProgressUpdate(batchStatus);
        }

        // ✅ FIXED: Check if all files are completed or failed using correct field names
        const allFilesFinished = batchStatus.files && 
          batchStatus.files.every(file => 
            file.status === 'completed' || file.status === 'failed'
          );

        if (allFilesFinished || batchStatus.is_completed) {
          return { success: true, data: batchStatus };
        } else if (batchStatus.overall_status === 'failed') {
          return { 
            success: false, 
            error: batchStatus.error || 'Batch processing failed',
            data: batchStatus
          };
        }

        // Wait 2-3 seconds before next poll
        await new Promise(resolve => setTimeout(resolve, 2500));
        attempts++;

      } catch (error) {
        console.error('Error during batch polling:', error);
        attempts++;
        
        // If we've tried many times, give up
        if (attempts >= 5) {
          return { 
            success: false, 
            error: 'Network error during batch processing. Please check your connection.' 
          };
        }
        
        // Wait a bit longer on error
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
    }

    // Timeout - show timeout handling options
    return { 
      success: false, 
      error: 'Batch processing is taking longer than expected.',
      timeout: true
    };
  }

  /**
   * Show school missing warning modal
   * Returns Promise<boolean> - true if user wants to proceed
   */
  async showSchoolMissingWarning() {
    return new Promise((resolve) => {
      // Create modal overlay
      const overlay = document.createElement('div');
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
      `;

      // Create modal content
      const modal = document.createElement('div');
      modal.style.cssText = `
        background: white;
        padding: 24px;
        border-radius: 8px;
        max-width: 400px;
        margin: 20px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      `;

      modal.innerHTML = `
        <h3 style="margin: 0 0 16px 0; color: #f59e0b;">⚠️ School Information Missing</h3>
        <p style="margin: 0 0 16px 0; line-height: 1.5;">
          We couldn't detect your school information. This might affect:
        </p>
        <ul style="margin: 0 0 20px 20px; line-height: 1.5;">
          <li>Data organization and filtering</li>
          <li>Access to school-specific features</li>
          <li>Proper categorization of results</li>
        </ul>
        <p style="margin: 0 0 20px 0; font-weight: 500;">
          Do you want to continue anyway?
        </p>
        <div style="display: flex; gap: 12px; justify-content: flex-end;">
          <button id="cancelBtn" style="
            padding: 8px 16px;
            border: 1px solid #d1d5db;
            background: white;
            border-radius: 4px;
            cursor: pointer;
          ">Cancel Upload</button>
          <button id="continueBtn" style="
            padding: 8px 16px;
            border: none;
            background: #f59e0b;
            color: white;
            border-radius: 4px;
            cursor: pointer;
          ">Continue Without School</button>
        </div>
      `;

      // Add event listeners
      const cancelBtn = modal.querySelector('#cancelBtn');
      const continueBtn = modal.querySelector('#continueBtn');

      cancelBtn.onclick = () => {
        document.body.removeChild(overlay);
        resolve(false);
      };

      continueBtn.onclick = () => {
        document.body.removeChild(overlay);
        resolve(true);
      };

      // Close on overlay click
      overlay.onclick = (e) => {
        if (e.target === overlay) {
          document.body.removeChild(overlay);
          resolve(false);
        }
      };

      overlay.appendChild(modal);
      document.body.appendChild(overlay);
    });
  }

  /**
   * Get analysis status and results
   * GET /api/surveys/analysis/{analysis_id}/
   */
  async getAnalysisStatus(analysisId, school) {
    try {
      const response = await fetch(`${API_BASE_URL}/surveys/analysis/${analysisId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          school: school
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get analysis status');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting analysis status:', error);
      throw error;
    }
  }

  /**
   * Delete analysis record
   * POST /api/surveys/analysis/{analysis_id}/delete
   */
  async deleteAnalysis(analysisId, options = {}) {
    try {
      const { school = 'UIUC' } = options;
      
      // 📊 Production logging for API calls
      console.log('🌐 API Delete Request:', { 
        analysisId, 
        school, 
        url: `${API_BASE_URL}/surveys/analysis/${analysisId}/delete`,
        timestamp: new Date().toISOString() 
      });
      
      const response = await fetch(`${API_BASE_URL}/surveys/analysis/${analysisId}/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          school: school
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete analysis');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting analysis:', error);
      throw error;
    }
  }

  /**
   * List all analyses
   * POST /api/surveys/analyses/
   */
  async listAnalyses(school) {
    try {
      const response = await fetch(`${API_BASE_URL}/surveys/analyses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
// <<<<<<< HEAD
// // <<<<<<< Updated upstream
          // school: school
//         })
// // =======
// //           school: school || 'UIUC'  // ✅ Updated to use school parameter
// //         }),
// // >>>>>>> Stashed changes
// =======
          school: school
        })
// >>>>>>> origin/main
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to list analyses');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error listing analyses:', error);
      throw error;
    }
  }

  /**
   * Poll for analysis completion
   * Polls every 2 seconds until status is 'completed' or 'failed'
   */
  async pollForCompletion(analysisId, school, onStatusUpdate = null) {
    const maxAttempts = 150; // 5 minutes max (150 * 2 seconds)
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const statusData = await this.getAnalysisStatus(analysisId, school);
        
        // Call status update callback if provided
        if (onStatusUpdate) {
          onStatusUpdate(statusData);
        }

        // Check if processing is complete
        if (statusData.status === 'completed') {
          return { success: true, data: statusData };
        } else if (statusData.status === 'failed') {
          return { 
            success: false, 
            error: statusData.error_message || 'Analysis failed' 
          };
        }

        // Wait 2 seconds before next poll
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;

      } catch (error) {
        console.error('Error during polling:', error);
        attempts++;
        
        // If we've tried many times, give up
        if (attempts >= 5) {
          return { 
            success: false, 
            error: 'Network error during analysis. Please check your connection.' 
          };
        }
        
        // Wait a bit longer on error
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
    }

    // Timeout
    return { 
      success: false, 
      error: 'Analysis is taking longer than expected. Please check back later.' 
    };
  }

  /**
   * Helper method to format error messages based on backend error constants
   */
  getFormattedErrorMessage(error) {
    const errorString = error.toString().toLowerCase();

    if (errorString.includes('openai') || errorString.includes('api')) {
      return "OpenAI is currently unavailable. Please try again later.";
    } else if (errorString.includes('excel') || errorString.includes('format')) {
      return "The Excel format must contain columns with 1-5 numerical ratings.";
    } else if (errorString.includes('size') || errorString.includes('large')) {
      return "File size must be less than 10MB.";
    } else if (errorString.includes('timeout')) {
      return "Processing took too long. Please try with a smaller file.";
    } else if (errorString.includes('questions') || errorString.includes('detect')) {
      return "Could not detect survey questions in the uploaded file.";
    } else {
      return error.message || 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Advanced retry logic with exponential backoff and error categorization
   */
  
  // Error categories for different retry strategies
  static ERROR_CATEGORIES = {
    NETWORK: 'network',
    SERVER: 'server', 
    VALIDATION: 'validation',
    PROCESSING: 'processing',
    TIMEOUT: 'timeout',
    UNKNOWN: 'unknown'
  };

  // Retry configurations for different error types
  static RETRY_CONFIGS = {
    [this.ERROR_CATEGORIES.NETWORK]: { maxAttempts: 3, delays: [0, 30000, 60000], retryable: true },
    [this.ERROR_CATEGORIES.SERVER]: { maxAttempts: 3, delays: [0, 30000, 60000], retryable: true },
    [this.ERROR_CATEGORIES.PROCESSING]: { maxAttempts: 2, delays: [0, 60000], retryable: true },
    [this.ERROR_CATEGORIES.TIMEOUT]: { maxAttempts: 2, delays: [0, 30000], retryable: true },
    [this.ERROR_CATEGORIES.VALIDATION]: { maxAttempts: 1, delays: [0], retryable: false },
    [this.ERROR_CATEGORIES.UNKNOWN]: { maxAttempts: 2, delays: [0, 30000], retryable: true }
  };

  /**
   * Categorize error based on error message and response
   */
  categorizeError(error, response = null) {
    const errorMessage = error.message?.toLowerCase() || '';
    const statusCode = response?.status;

    // Network errors
    if (error.name === 'TypeError' || errorMessage.includes('network') || 
        errorMessage.includes('fetch') || errorMessage.includes('connection')) {
      return OpenAIService.ERROR_CATEGORIES.NETWORK;
    }

    // Server errors (5xx)
    if (statusCode >= 500) {
      return OpenAIService.ERROR_CATEGORIES.SERVER;
    }

    // Validation errors (4xx except timeout)
    if (statusCode >= 400 && statusCode < 500 && statusCode !== 408) {
      return OpenAIService.ERROR_CATEGORIES.VALIDATION;
    }

    // Timeout errors
    if (statusCode === 408 || errorMessage.includes('timeout') || 
        errorMessage.includes('taking longer')) {
      return OpenAIService.ERROR_CATEGORIES.TIMEOUT;
    }

    // Processing errors
    if (errorMessage.includes('processing') || errorMessage.includes('analysis') ||
        errorMessage.includes('excel') || errorMessage.includes('data')) {
      return OpenAIService.ERROR_CATEGORIES.PROCESSING;
    }

    return OpenAIService.ERROR_CATEGORIES.UNKNOWN;
  }

  /**
   * Check if error is retryable based on category
   */
  isRetryable(errorCategory) {
    const config = OpenAIService.RETRY_CONFIGS[errorCategory];
    return config ? config.retryable : false;
  }

  /**
   * Get retry configuration for error category
   */
  getRetryConfig(errorCategory) {
    return OpenAIService.RETRY_CONFIGS[errorCategory] || 
           OpenAIService.RETRY_CONFIGS[OpenAIService.ERROR_CATEGORIES.UNKNOWN];
  }

  /**
   * Execute operation with advanced retry logic
   */
  async executeWithRetry(operation, operationName = 'operation', onRetryUpdate = null) {
    let lastError = null;
    let attempt = 0;
    const retryHistory = [];

    while (attempt < 3) { // Max 3 attempts total
      try {
        const startTime = Date.now();
        const result = await operation();
        
        // Log successful retry if this wasn't the first attempt
        if (attempt > 0) {
          console.log(`✅ ${operationName} succeeded on attempt ${attempt + 1}`);
          if (onRetryUpdate) {
            onRetryUpdate({
              type: 'success',
              attempt: attempt + 1,
              totalAttempts: attempt + 1,
              duration: Date.now() - startTime,
              retryHistory
            });
          }
        }
        
        return { success: true, data: result, attempts: attempt + 1, retryHistory };

      } catch (error) {
        attempt++;
        lastError = error;
        
        // Categorize the error
        const errorCategory = this.categorizeError(error);
        const retryConfig = this.getRetryConfig(errorCategory);
        const isRetryable = this.isRetryable(errorCategory);
        
        // Add to retry history
        const retryInfo = {
          attempt,
          error: error.message,
          errorCategory,
          timestamp: new Date().toISOString(),
          isRetryable,
          willRetry: attempt < retryConfig.maxAttempts && isRetryable
        };
        retryHistory.push(retryInfo);

        console.warn(`❌ ${operationName} failed on attempt ${attempt}:`, {
          error: error.message,
          category: errorCategory,
          retryable: isRetryable,
          willRetry: retryInfo.willRetry
        });

        // Notify about retry attempt
        if (onRetryUpdate) {
          onRetryUpdate({
            type: 'error',
            attempt,
            error: error.message,
            errorCategory,
            isRetryable,
            willRetry: retryInfo.willRetry,
            retryHistory
          });
        }

        // Check if we should retry
        if (attempt >= retryConfig.maxAttempts || !isRetryable) {
          console.error(`💥 ${operationName} failed permanently after ${attempt} attempts`);
          break;
        }

        // Calculate delay for this attempt
        const delayIndex = Math.min(attempt - 1, retryConfig.delays.length - 1);
        const delay = retryConfig.delays[delayIndex];

        if (delay > 0) {
          console.log(`⏳ Waiting ${delay / 1000}s before retry ${attempt + 1}...`);
          
          // Notify about delay
          if (onRetryUpdate) {
            onRetryUpdate({
              type: 'delay',
              attempt,
              delay,
              nextAttempt: attempt + 1,
              retryHistory
            });
          }

          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All retries exhausted
    return { 
      success: false, 
      error: lastError, 
      attempts: attempt,
      retryHistory,
      finalErrorCategory: this.categorizeError(lastError)
    };
  }

  /**
   * Retry single file with advanced retry logic
   * 
   * 🔧 FIXED: Moved polling into the operation to prevent duplicate database records
   * Previously: Upload success → Retry stops → Separate polling might fail → Creates duplicates
   * Now: Upload + polling success → Retry stops → No duplicates
   */
  async retryFileWithAdvancedLogic(file, analysisType, courseMapping, programName, school, onRetryUpdate = null) {
    console.log('🔄 Starting advanced retry for file:', file.name);

    // ❌ ORIGINAL LOGIC (commented out - prevents duplicate database records issue):
    // Issue: executeWithRetry considers upload API success as complete success,
    // but separate polling can fail, causing retries that create duplicate DB records
    /*
    const operation = async () => {
      return await this.batchImportExcel([file], analysisType, courseMapping, programName, school);
    };
    */

    // ✅ RETRY-FIX: Include polling in operation to prevent duplicate database records
    // Now retry only happens when the ENTIRE process (upload + processing) fails
    const operation = async () => {
      console.log(`🔄 [RETRY-FIX] Starting complete operation (upload + polling) for ${file.name}`);
      
      // Step 1: Start the batch upload
      const uploadResult = await this.batchImportExcel([file], analysisType, courseMapping, programName, school);
      console.log(`✅ [RETRY-FIX] Upload succeeded for ${file.name}, BatchId: ${uploadResult.batchId}, now polling for completion...`);
      
      // Step 2: Wait for completion - THIS is now part of the operation
      const completionResult = await this.pollForBatchCompletion(
        uploadResult.batchId,
        school,
        (progressData) => {
          if (onRetryUpdate) {
            onRetryUpdate({
              type: 'progress',
              batchId: uploadResult.batchId,
              progressData
            });
          }
        }
      );
      
      // Step 3: Only return success if completion succeeded
      if (!completionResult.success) {
        const errorMsg = `Batch processing failed after upload succeeded. BatchId: ${uploadResult.batchId}. Error: ${completionResult.error || 'Processing timeout or unknown error'}`;
        console.warn(`⚠️ [RETRY-FIX] ${errorMsg}`);
        throw new Error(errorMsg);
      }
      
      console.log(`🎉 [RETRY-FIX] Complete success for ${file.name}, BatchId: ${uploadResult.batchId} - This should prevent duplicate records`);
      
      // Return the completion result
      return {
        ...completionResult,
        batchId: uploadResult.batchId,
        originalUpload: uploadResult
      };
    };

    const retryResult = await this.executeWithRetry(
      operation, 
      `File upload: ${file.name}`,
      onRetryUpdate
    );

    // ❌ ORIGINAL LOGIC (commented out - part of duplicate records fix):
    /*
    if (retryResult.success) {
      // Poll for completion of the retry
      const pollResult = await this.pollForBatchCompletion(
        retryResult.data.batchId,
        school,
        (progressData) => {
          if (onRetryUpdate) {
            onRetryUpdate({
              type: 'progress',
              batchId: retryResult.data.batchId,
              progressData,
              retryHistory: retryResult.retryHistory
            });
          }
        }
      );

      return {
        ...pollResult,
        retryHistory: retryResult.retryHistory,
        totalAttempts: retryResult.attempts
      };
    }

    return retryResult;
    */

    // ✅ RETRY-FIX: Simplified logic since polling is now part of the operation
    console.log(`📊 [RETRY-FIX] Retry process completed for ${file.name}, success: ${retryResult.success}, attempts: ${retryResult.attempts}`);

    if (retryResult.success) {
      return {
        ...retryResult.data,
        retryHistory: retryResult.retryHistory,
        totalAttempts: retryResult.attempts
      };
    }

    return retryResult;
  }

  /**
   * Retry multiple files with advanced retry logic and queue management
   */
  async retryMultipleFilesWithAdvancedLogic(files, analysisType, courseMapping, programName, school, onRetryUpdate = null) {
    console.log('🔄 Starting advanced retry for multiple files:', files.map(f => f.name));

    const retryQueue = files.map(file => ({
      file,
      status: 'queued',
      attempts: 0,
      retryHistory: [],
      error: null
    }));

    const results = {
      successful: [],
      failed: [],
      totalFiles: files.length,
      overallRetryHistory: []
    };

    // Process files sequentially to avoid overwhelming the server
    for (let i = 0; i < retryQueue.length; i++) {
      const queueItem = retryQueue[i];
      queueItem.status = 'processing';

      if (onRetryUpdate) {
        onRetryUpdate({
          type: 'queue_update',
          currentFile: queueItem.file.name,
          currentIndex: i,
          totalFiles: files.length,
          queue: retryQueue.map(item => ({
            filename: item.file.name,
            status: item.status,
            attempts: item.attempts
          }))
        });
      }

      try {
        const fileRetryResult = await this.retryFileWithAdvancedLogic(
          queueItem.file,
          analysisType,
          courseMapping[queueItem.file.name] ? { [queueItem.file.name]: courseMapping[queueItem.file.name] } : {},
          programName,
          school,
          (retryUpdate) => {
            // Forward retry updates with queue context
            if (onRetryUpdate) {
              onRetryUpdate({
                ...retryUpdate,
                currentFile: queueItem.file.name,
                currentIndex: i,
                totalFiles: files.length
              });
            }
          }
        );

        queueItem.attempts = fileRetryResult.totalAttempts || fileRetryResult.attempts || 1;
        queueItem.retryHistory = fileRetryResult.retryHistory || [];

        if (fileRetryResult.success) {
          queueItem.status = 'completed';
          results.successful.push({
            file: queueItem.file,
            result: fileRetryResult.data,
            attempts: queueItem.attempts,
            retryHistory: queueItem.retryHistory
          });
        } else {
          queueItem.status = 'failed';
          queueItem.error = fileRetryResult.error;
          results.failed.push({
            file: queueItem.file,
            error: fileRetryResult.error,
            attempts: queueItem.attempts,
            retryHistory: queueItem.retryHistory,
            errorCategory: fileRetryResult.finalErrorCategory
          });
        }

      } catch (error) {
        queueItem.status = 'failed';
        queueItem.error = error;
        results.failed.push({
          file: queueItem.file,
          error: error,
          attempts: 1,
          retryHistory: [{ attempt: 1, error: error.message, timestamp: new Date().toISOString() }],
          errorCategory: this.categorizeError(error)
        });
      }

      // Add to overall retry history
      results.overallRetryHistory.push({
        filename: queueItem.file.name,
        attempts: queueItem.attempts,
        status: queueItem.status,
        retryHistory: queueItem.retryHistory
      });
    }

    console.log('🏁 Advanced retry completed:', {
      successful: results.successful.length,
      failed: results.failed.length,
      total: results.totalFiles
    });

    return results;
  }

  /**
   * Advanced timeout handling and recovery system
   */
  
  // Timeout configurations for different operations
  static TIMEOUT_CONFIGS = {
    SINGLE_FILE: {
      warning: 120000,    // 2 minutes - show warning
      soft: 300000,       // 5 minutes - offer options
      hard: 600000        // 10 minutes - force timeout
    },
    BATCH_SMALL: {       // 1-3 files
      warning: 180000,    // 3 minutes
      soft: 450000,       // 7.5 minutes
      hard: 900000        // 15 minutes
    },
    BATCH_MEDIUM: {      // 4-7 files
      warning: 300000,    // 5 minutes
      soft: 600000,       // 10 minutes
      hard: 1200000       // 20 minutes
    },
    BATCH_LARGE: {       // 8-10 files
      warning: 420000,    // 7 minutes
      soft: 900000,       // 15 minutes
      hard: 1800000       // 30 minutes
    }
  };

  /**
   * Get timeout configuration based on operation type and file count
   */
  getTimeoutConfig(fileCount = 1) {
    if (fileCount === 1) {
      return OpenAIService.TIMEOUT_CONFIGS.SINGLE_FILE;
    } else if (fileCount <= 3) {
      return OpenAIService.TIMEOUT_CONFIGS.BATCH_SMALL;
    } else if (fileCount <= 7) {
      return OpenAIService.TIMEOUT_CONFIGS.BATCH_MEDIUM;
    } else {
      return OpenAIService.TIMEOUT_CONFIGS.BATCH_LARGE;
    }
  }

  /**
   * Enhanced polling with timeout handling and recovery options
   */
  async pollWithTimeoutHandling(batchId, school, fileCount, onProgressUpdate = null, onTimeoutWarning = null) {
    const timeoutConfig = this.getTimeoutConfig(fileCount);
    const startTime = Date.now();
    let attempts = 0;
    let warningShown = false;
    let softTimeoutShown = false;

    console.log('⏱️ Starting polling with timeout handling:', {
      batchId,
      fileCount,
      timeouts: {
        warning: `${timeoutConfig.warning / 1000}s`,
        soft: `${timeoutConfig.soft / 1000}s`,
        hard: `${timeoutConfig.hard / 1000}s`
      }
    });

    while (true) {
      const elapsed = Date.now() - startTime;
      
      // Check for hard timeout
      if (elapsed >= timeoutConfig.hard) {
        console.warn('💥 Hard timeout reached:', elapsed / 1000, 'seconds');
        return {
          success: false,
          error: 'Processing timeout exceeded maximum allowed time',
          timeout: true,
          timeoutType: 'hard',
          elapsed,
          batchId
        };
      }

      try {
        const batchStatus = await this.getBatchAnalysisStatus(batchId, school);
        
        // Call progress update callback with timeout info
        if (onProgressUpdate) {
          onProgressUpdate({
            ...batchStatus,
            timeoutInfo: {
              elapsed,
              warning: timeoutConfig.warning,
              soft: timeoutConfig.soft,
              hard: timeoutConfig.hard,
              warningShown,
              softTimeoutShown
            }
          });
        }

        // ✅ FIXED: Check if processing is complete using correct field names
        const allFilesFinished = batchStatus.files && 
          batchStatus.files.every(file => 
            file.status === 'completed' || file.status === 'failed'
          );

        if (allFilesFinished || batchStatus.is_completed) {
          console.log('✅ Polling completed successfully after', elapsed / 1000, 'seconds');
          return { success: true, data: batchStatus, elapsed };
        } else if (batchStatus.overall_status === 'failed') {
          return { 
            success: false, 
            error: batchStatus.error || 'Batch processing failed',
            data: batchStatus,
            elapsed
          };
        }

        // Check for warning timeout
        if (!warningShown && elapsed >= timeoutConfig.warning) {
          warningShown = true;
          console.warn('⚠️ Warning timeout reached:', elapsed / 1000, 'seconds');
          
          if (onTimeoutWarning) {
            onTimeoutWarning({
              type: 'warning',
              elapsed,
              remaining: timeoutConfig.soft - elapsed,
              message: `Processing is taking longer than expected (${Math.round(elapsed / 1000)}s). This is normal for large files or complex data.`
            });
          }
        }

        // Check for soft timeout - offer recovery options
        if (!softTimeoutShown && elapsed >= timeoutConfig.soft) {
          softTimeoutShown = true;
          console.warn('⏰ Soft timeout reached:', elapsed / 1000, 'seconds');
          
          if (onTimeoutWarning) {
            const recoveryOptions = await onTimeoutWarning({
              type: 'soft',
              elapsed,
              remaining: timeoutConfig.hard - elapsed,
              batchId,
              batchStatus,
              message: `Processing has taken ${Math.round(elapsed / 1000)}s. What would you like to do?`,
              options: ['continue', 'background', 'cancel']
            });

            // Handle recovery option
            if (recoveryOptions === 'cancel') {
              return {
                success: false,
                error: 'Processing cancelled by user',
                cancelled: true,
                elapsed,
                batchId
              };
            } else if (recoveryOptions === 'background') {
              return {
                success: false,
                error: 'Processing moved to background',
                background: true,
                elapsed,
                batchId,
                batchStatus
              };
            }
            // 'continue' option falls through to keep polling
          }
        }

        // Wait before next poll (adaptive timing based on elapsed time)
        const pollInterval = elapsed < 60000 ? 2500 : // First minute: 2.5s
                           elapsed < 300000 ? 5000 : // First 5 minutes: 5s
                           10000; // After 5 minutes: 10s
        
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempts++;

      } catch (error) {
        console.error('Error during timeout-aware polling:', error);
        attempts++;
        
        // Network error handling with timeout context
        if (attempts >= 5) {
          return { 
            success: false, 
            error: 'Network error during processing. Please check your connection.',
            networkError: true,
            elapsed
          };
        }
        
        // Wait longer on error
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
    }
  }

  /**
   * Background processing state management
   */
  static backgroundProcesses = new Map();

  /**
   * Start background processing for a batch
   */
  startBackgroundProcessing(batchId, school, fileCount, metadata = {}) {
    const backgroundProcess = {
      batchId,
      school,
      fileCount,
      startTime: Date.now(),
      metadata,
      status: 'processing',
      lastCheck: Date.now(),
      checkInterval: null
    };

    // Set up periodic checking
    backgroundProcess.checkInterval = setInterval(async () => {
      try {
        const batchStatus = await this.getBatchAnalysisStatus(batchId, school);
        backgroundProcess.lastCheck = Date.now();
        backgroundProcess.status = batchStatus.overall_status;

        // ✅ FIXED: Check if completed using correct field names
        const allFilesFinished = batchStatus.files && 
          batchStatus.files.every(file => 
            file.status === 'completed' || file.status === 'failed'
          );

        if (allFilesFinished || batchStatus.is_completed || batchStatus.overall_status === 'failed') {
          backgroundProcess.status = 'completed';
          backgroundProcess.result = batchStatus;
          clearInterval(backgroundProcess.checkInterval);
          
          // Notify user if possible
          this.notifyBackgroundCompletion(batchId, batchStatus);
        }
      } catch (error) {
        console.error('Background process check failed:', error);
        backgroundProcess.lastError = error;
      }
    }, 30000); // Check every 30 seconds

    OpenAIService.backgroundProcesses.set(batchId, backgroundProcess);
    console.log('🔄 Started background processing for batch:', batchId);
    
    return backgroundProcess;
  }

  /**
   * Get background process status
   */
  getBackgroundProcess(batchId) {
    return OpenAIService.backgroundProcesses.get(batchId);
  }

  /**
   * Get all background processes
   */
  getAllBackgroundProcesses() {
    return Array.from(OpenAIService.backgroundProcesses.values());
  }

  /**
   * Stop background processing
   */
  stopBackgroundProcessing(batchId) {
    const process = OpenAIService.backgroundProcesses.get(batchId);
    if (process && process.checkInterval) {
      clearInterval(process.checkInterval);
      OpenAIService.backgroundProcesses.delete(batchId);
      console.log('⏹️ Stopped background processing for batch:', batchId);
      return true;
    }
    return false;
  }

  /**
   * Notify user of background completion
   */
  notifyBackgroundCompletion(batchId, batchStatus) {
    // Try to show browser notification if permission granted
    if ('Notification' in window && Notification.permission === 'granted') {
      const successCount = batchStatus.files ? 
        batchStatus.files.filter(f => f.status === 'completed').length : 0;
      const totalCount = batchStatus.files ? batchStatus.files.length : 0;
      
      new Notification('Survey Analysis Complete', {
        body: `${successCount}/${totalCount} files processed successfully`,
        icon: '/favicon.ico',
        tag: `batch-${batchId}`
      });
    }

    // Also try to dispatch custom event for app to handle
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('backgroundProcessComplete', {
        detail: { batchId, batchStatus }
      }));
    }
  }

  /**
   * Request notification permission for background processing
   */
  async requestNotificationPermission() {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }

  /**
   * Resume background process - bring it back to foreground
   */
  async resumeBackgroundProcess(batchId, onProgressUpdate = null) {
    const backgroundProcess = this.getBackgroundProcess(batchId);
    if (!backgroundProcess) {
      throw new Error('Background process not found');
    }

    console.log('🔄 Resuming background process:', batchId);

    // Stop background checking
    if (backgroundProcess.checkInterval) {
      clearInterval(backgroundProcess.checkInterval);
    }

    // If already completed, return result
    if (backgroundProcess.status === 'completed' && backgroundProcess.result) {
      OpenAIService.backgroundProcesses.delete(batchId);
      return { success: true, data: backgroundProcess.result };
    }

    // Resume normal polling
    const resumeResult = await this.pollWithTimeoutHandling(
      batchId,
      backgroundProcess.school,
      backgroundProcess.fileCount,
      onProgressUpdate
    );

    // Clean up background process
    OpenAIService.backgroundProcesses.delete(batchId);
    return resumeResult;
  }

  /**
   * Enhanced batch import with timeout handling
   */
  async batchImportWithTimeoutHandling(files, analysisType, courseMapping, programName, school, onProgressUpdate = null, onTimeoutWarning = null) {
    console.log('🚀 Starting batch import with timeout handling:', files.length, 'files');

    // Start the batch upload
    const batchResponse = await this.batchImportExcel(files, analysisType, courseMapping, programName, school);
    
    // Use timeout-aware polling
    return await this.pollWithTimeoutHandling(
      batchResponse.batchId,
      school,
      files.length,
      onProgressUpdate,
      onTimeoutWarning
    );
  }

  /**
   * Performance monitoring and optimization
   */
   
   // Performance metrics tracking
   static performanceMetrics = {
     uploads: [],
     polling: [],
     retries: [],
     memory: []
   };

   /**
    * Track upload performance metrics
    */
   trackUploadPerformance(fileCount, fileSize, duration, success) {
     const metric = {
       timestamp: Date.now(),
       fileCount,
       totalSize: fileSize,
       duration,
       success,
       throughput: fileSize / (duration / 1000), // bytes per second
       avgFileSize: fileSize / fileCount
     };

     OpenAIService.performanceMetrics.uploads.push(metric);
     
     // Keep only last 100 metrics to prevent memory bloat
     if (OpenAIService.performanceMetrics.uploads.length > 100) {
       OpenAIService.performanceMetrics.uploads.shift();
     }

     console.log('📊 Upload performance:', {
       files: fileCount,
       size: `${(fileSize / 1024 / 1024).toFixed(2)}MB`,
       duration: `${(duration / 1000).toFixed(1)}s`,
       throughput: `${(metric.throughput / 1024 / 1024).toFixed(2)}MB/s`
     });

     return metric;
   }

   /**
    * Track polling performance metrics
    */
   trackPollingPerformance(batchId, duration, pollCount, success) {
     const metric = {
       timestamp: Date.now(),
       batchId,
       duration,
       pollCount,
       success,
       avgPollInterval: duration / pollCount
     };

     OpenAIService.performanceMetrics.polling.push(metric);
     
     if (OpenAIService.performanceMetrics.polling.length > 100) {
       OpenAIService.performanceMetrics.polling.shift();
     }

     console.log('📊 Polling performance:', {
       batchId,
       duration: `${(duration / 1000).toFixed(1)}s`,
       polls: pollCount,
       avgInterval: `${(metric.avgPollInterval / 1000).toFixed(1)}s`
     });

     return metric;
   }

   /**
    * Track memory usage
    */
   trackMemoryUsage() {
     if (performance.memory) {
       const memory = {
         timestamp: Date.now(),
         used: performance.memory.usedJSHeapSize,
         total: performance.memory.totalJSHeapSize,
         limit: performance.memory.jsHeapSizeLimit
       };

       OpenAIService.performanceMetrics.memory.push(memory);
       
       if (OpenAIService.performanceMetrics.memory.length > 50) {
         OpenAIService.performanceMetrics.memory.shift();
       }

       // Warn if memory usage is high
       const usagePercent = (memory.used / memory.limit) * 100;
       if (usagePercent > 80) {
         console.warn('⚠️ High memory usage detected:', {
           used: `${(memory.used / 1024 / 1024).toFixed(2)}MB`,
           percent: `${usagePercent.toFixed(1)}%`
         });
       }

       return memory;
     }
     return null;
   }

   /**
    * Get performance analytics
    */
   getPerformanceAnalytics() {
     const uploads = OpenAIService.performanceMetrics.uploads;
     const polling = OpenAIService.performanceMetrics.polling;
     const memory = OpenAIService.performanceMetrics.memory;

     const analytics = {
       uploads: {
         total: uploads.length,
         successful: uploads.filter(u => u.success).length,
         avgDuration: uploads.length > 0 ? uploads.reduce((sum, u) => sum + u.duration, 0) / uploads.length : 0,
         avgThroughput: uploads.length > 0 ? uploads.reduce((sum, u) => sum + u.throughput, 0) / uploads.length : 0,
         totalFiles: uploads.reduce((sum, u) => sum + u.fileCount, 0),
         totalSize: uploads.reduce((sum, u) => sum + u.totalSize, 0)
       },
       polling: {
         total: polling.length,
         successful: polling.filter(p => p.success).length,
         avgDuration: polling.length > 0 ? polling.reduce((sum, p) => sum + p.duration, 0) / polling.length : 0,
         avgPollCount: polling.length > 0 ? polling.reduce((sum, p) => sum + p.pollCount, 0) / polling.length : 0
       },
       memory: {
         current: memory.length > 0 ? memory[memory.length - 1] : null,
         peak: memory.length > 0 ? Math.max(...memory.map(m => m.used)) : 0,
         avg: memory.length > 0 ? memory.reduce((sum, m) => sum + m.used, 0) / memory.length : 0
       }
     };

     return analytics;
   }

   /**
    * Optimize memory usage by cleaning up old data
    */
   optimizeMemory() {
     console.log('🧹 Optimizing memory usage...');
     
     // Clear old background processes
     const now = Date.now();
     const maxAge = 24 * 60 * 60 * 1000; // 24 hours
     
     for (const [batchId, process] of OpenAIService.backgroundProcesses.entries()) {
       if (now - process.startTime > maxAge) {
         this.stopBackgroundProcessing(batchId);
         console.log('🗑️ Cleaned up old background process:', batchId);
       }
     }

     // Limit performance metrics
     const maxMetrics = 50;
     Object.keys(OpenAIService.performanceMetrics).forEach(key => {
       const metrics = OpenAIService.performanceMetrics[key];
       if (metrics.length > maxMetrics) {
         OpenAIService.performanceMetrics[key] = metrics.slice(-maxMetrics);
       }
     });

     // Force garbage collection if available
     if (window.gc) {
       window.gc();
       console.log('🗑️ Forced garbage collection');
     }

     this.trackMemoryUsage();
   }

   /**
    * Enhanced batch import with performance tracking
    */
   async batchImportWithPerformanceTracking(files, analysisType, courseMapping, programName, school, onProgressUpdate = null, onTimeoutWarning = null) {
     const startTime = Date.now();
     const totalSize = files.reduce((sum, file) => sum + file.size, 0);
     
     console.log('🚀 Starting performance-tracked batch import:', {
       files: files.length,
       totalSize: `${(totalSize / 1024 / 1024).toFixed(2)}MB`
     });

     // Track initial memory
     this.trackMemoryUsage();

     try {
       const result = await this.batchImportWithTimeoutHandling(
         files,
         analysisType,
         courseMapping,
         programName,
         school,
         (progressData) => {
           // Track memory during processing
           if (Math.random() < 0.1) { // 10% chance to avoid overhead
             this.trackMemoryUsage();
           }
           
           if (onProgressUpdate) {
             onProgressUpdate(progressData);
           }
         },
         onTimeoutWarning
       );

       // Track successful upload performance
       const duration = Date.now() - startTime;
       this.trackUploadPerformance(files.length, totalSize, duration, result.success);

       return result;

     } catch (error) {
       // Track failed upload performance
       const duration = Date.now() - startTime;
       this.trackUploadPerformance(files.length, totalSize, duration, false);
       throw error;
     }
   }

   /**
    * Debounced function utility for performance optimization
    */
   debounce(func, wait) {
     let timeout;
     return function executedFunction(...args) {
       const later = () => {
         clearTimeout(timeout);
         func(...args);
       };
       clearTimeout(timeout);
       timeout = setTimeout(later, wait);
     };
   }

   /**
    * Throttled function utility for performance optimization
    */
   throttle(func, limit) {
     let inThrottle;
     return function(...args) {
       if (!inThrottle) {
         func.apply(this, args);
         inThrottle = true;
         setTimeout(() => inThrottle = false, limit);
       }
     };
   }
}

// Export singleton instance
export const openAIService = new OpenAIService();
export default openAIService; 